// Day.js
import dayjs from "dayjs";
// FHIR front library
import { SearchableTable } from "@fyrstain/hl7-front-library";
// Font awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Navigation
import { useNavigate } from "react-router-dom";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback } from "react";
// FHIR
import { Encounter } from "fhir/r4";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";

const VisitsPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const onDetails = useCallback(
    (id: string) => {
      navigate(`/Pathway/Visits/${id}`);
    },
    [navigate],
  );

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "Encounter visit error:",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function getPatient(encounter: Encounter): string {
    return (
      encounter.subject?.display ??
      encounter.subject?.identifier?.value ??
      encounter.subject?.reference ??
      i18n.t("general.notavailable")
    );
  }

  function formatDate(value?: string): string {
    if (!value) {
      return i18n.t("general.notavailable");
    }

    return dayjs(value).format("DD/MM/YYYY HH:mm");
  }

  function getType(encounter: Encounter): string {
    return (
      encounter.type?.[0]?.text ??
      encounter.type?.[0]?.coding?.[0]?.display ??
      encounter.type?.[0]?.coding?.[0]?.code ??
      i18n.t("general.notavailable")
    );
  }

  function getStatus(encounter: Encounter): string {
    if (!encounter.status) {
      return i18n.t("general.notavailable");
    }

    return i18n.t(`encounter.status.${encounter.status}`, {
      defaultValue: encounter.status,
    });
  }

  function getServiceProvider(encounter: Encounter): string {
    return (
      encounter.serviceProvider?.display ??
      encounter.serviceProvider?.identifier?.value ??
      encounter.serviceProvider?.reference ??
      i18n.t("general.notavailable")
    );
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
  <AcesoPage
    loading={false}
    titleKey="pathway.tabs.visits"
    needsLogin={true}
  >
    <SearchableTable
      searchCriteriaProperties={{
        title: i18n.t("title.searchcriteria"),
        submitButtonLabel: i18n.t("button.search"),
        resetButtonLabel: i18n.t("button.reset"),
        language: i18n.t,
        fixedParameters: {
          _sort: "-date",
        },
        inputs: [
          {
            label: i18n.t("label.patient"),
            type: "text",
            searchParamsName: "subject.name",
            placeholder: i18n.t("placeholder.patientname"),
          },
          {
            label: i18n.t("label.date"),
            type: "date",
            searchParamsName: "date",
          },
          {
            label: i18n.t("label.status"),
            type: "select",
            searchParamsName: "status",
            options: [
              {
                label: i18n.t("encounter.status.planned"),
                value: "planned",
              },
              {
                label: i18n.t("encounter.status.arrived"),
                value: "arrived",
              },
              {
                label: i18n.t("encounter.status.triaged"),
                value: "triaged",
              },
              {
                label: i18n.t("encounter.status.in-progress"),
                value: "in-progress",
              },
              {
                label: i18n.t("encounter.status.onleave"),
                value: "onleave",
              },
              {
                label: i18n.t("encounter.status.finished"),
                value: "finished",
              },
              {
                label: i18n.t("encounter.status.cancelled"),
                value: "cancelled",
              },
              {
                label: i18n.t("encounter.status.entered-in-error"),
                value: "entered-in-error",
              },
              {
                label: i18n.t("encounter.status.unknown"),
                value: "unknown",
              },
            ],
          },
        ],
      }}
      paginatedTableProperties={{
        columns: [
          {
            header: i18n.t("label.patient"),
            dataField: "patient",
            width: "18%",
          },
          {
            header: i18n.t("label.date"),
            dataField: "date",
            width: "18%",
          },
          {
            header: i18n.t("label.type"),
            dataField: "type",
            width: "22%",
          },
          {
            header: i18n.t("label.status"),
            dataField: "status",
            width: "14%",
          },
          {
            header: i18n.t("label.serviceprovider"),
            dataField: "serviceProvider",
            width: "20%",
          },
        ],
        action: [
          {
            icon: faEye,
            onClick: onDetails,
          },
        ],
        mapResourceToData: (resource: Encounter) => ({
          id: resource.id,
          patient: getPatient(resource),
          date: formatDate(resource.period?.start),
          type: getType(resource),
          status: getStatus(resource),
          serviceProvider: getServiceProvider(resource),
        }),
        searchProperties: {
          serverUrl: process.env.REACT_APP_FHIR_URL as string,
          resourceType: "Encounter",
        },
        onError,
      }}
    />
  </AcesoPage>
);
};

export default VisitsPage;